"use client";

import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import Image from "next/image";
import Link from "next/link";
import { ExternalLink } from "lucide-react";
import { extractHostname, formatDisplayUrl } from "@/lib/url";
interface OverallScoreProps {
  score: number;
  url: string;
  createdAt: string | Date;
  className?: string;
  hideFavicon?: boolean;
}

export function OverallScore({
  score,
  url,
  createdAt,
  className = "",
  hideFavicon = false,
}: OverallScoreProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return "#10b981"; // green-500
    if (score >= 60) return "#f97316"; // orange-500
    return "#ef4444"; // red-500
  };

  // Extract hostname for favicon and clean URL for display
  const { hostname, href } = extractHostname(url);
  const displayValue = formatDisplayUrl(url);
  const linkHref = href;

  // Format creation date
  const createdDate = new Date(createdAt);

  const chartData = [
    { name: "Score", value: score, fill: getScoreColor(score) },
    {
      name: "Remaining",
      value: 100 - score,
      fill: "transparent",
    },
  ];

  return (
    <div className={`text-center space-y-6 ${className}`}>
      {/* Website Info */}
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-3">
          {hostname && !hideFavicon && (
            <Image
              src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=32`}
              alt="Favicon"
              width={24}
              height={24}
              className="!max-w-none rounded-md shadow-sm"
              unoptimized
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
          <div className="flex items-center gap-2">
            {linkHref ? (
              <Link
                href={linkHref}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="group inline-flex items-center gap-2 font-bold text-foreground text-xl tracking-tight transition-colors hover:text-primary"
              >
                <span className="transition-colors group-hover:text-primary group-hover:underline group-hover:underline-offset-4">
                  {displayValue || "Website"}
                </span>
                <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
              </Link>
            ) : (
              <span className="font-bold text-foreground text-xl tracking-tight">
                {displayValue || "Website"}
              </span>
            )}
          </div>
        </div>
        <div className="text-muted-foreground/80 text-sm font-medium">
          {createdDate.toISOString().split("T")[0].replace(/-/g, "/")}
        </div>
      </div>

      {/* Chart */}
      <div className="relative h-48 sm:h-56 w-full mb-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={78}
              strokeWidth={0}
              cornerRadius={12}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              animationDuration={1200}
              animationEasing="ease-out"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-4xl sm:text-5xl font-black tracking-tight text-foreground">
            {score}
          </div>
        </div>
      </div>

      {/* Title under chart */}
      <div className="space-y-2">
        <h3 className="text-lg font-bold tracking-tight text-foreground">
          Overall Score
        </h3>
      </div>
    </div>
  );
}
